import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ArrowDown, ArrowUpRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Reveal, EASE } from '../lib/motion';
import Seo from '../components/site/Seo';
import { SectionHeading } from '../components/site/SectionHeading';
import EditorialMarquee from '../components/site/Marquee';
import {
  IMAGES,
  STATS,
  PRODUCTS,
  WHY,
  CHAPTERS,
  CERTIFICATIONS,
  INDUSTRIES,
  TESTIMONIALS,
  NEWS,
  EXPORT_REGIONS,
} from '../data/content';

const HERO_LINES = ['We make the', 'heart of the', 'transformer.'];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ['0%', '-15%']);

  return (
    <section ref={ref} className="relative h-[78svh] min-h-[520px] overflow-hidden bg-navy">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.heroHome}
          alt="Banmore Electricals CRGO transformer core manufacturing facility"
          className="h-full w-full object-cover opacity-40"
          
        />
      </motion.div>
      <div
        className="banner-breathe absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-navy/20"
        style={{ '--breathe-min': 0.05 }}
      />
      <div className="grain absolute inset-0" />

      <motion.div style={{ y: overlayY }} className="container-x relative flex h-full flex-col justify-end pb-14 pt-24">
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="mono-label text-brand-light"
        >
          Banmore Electricals · CRGO Transformer Core
        </motion.span>

        <h1 className="banner-text mt-6 font-display text-[12vw] font-semibold leading-[0.95] tracking-tighter text-white sm:text-[10vw] lg:text-[7vw]">
          {HERO_LINES.map((line, i) => (
            <span key={i} className="hero-mask-line">
              <motion.span
                className="block"
                initial={{ y: '110%' }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.14, duration: 1, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8, ease: EASE }}
          className="mt-8 flex flex-col items-start gap-6 sm:flex-row sm:items-center"
        >
          <p className="banner-text max-w-md text-base leading-relaxed text-white/80">
            A leading global manufacturer & exporter of CRGO transformer core lamination and
            built-core assembly — with guaranteed iron losses, since 1977.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/products">
              <Button
                data-testid="hero-products-btn"
                className="btn-sweep group h-12 rounded-none bg-brand px-7 text-white hover:bg-brand"
              >
                View Products
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 1 }}
        className="absolute bottom-8 right-8 hidden items-center gap-3 text-white/50 md:flex"
      >
        <span className="mono-label">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown className="h-4 w-4" />
        </motion.div>
      </motion.div>
    </section>
  );
}

export default function Home() {

  return (
    <>
      <Seo page="home" />
      <Hero />
      <EditorialMarquee />

      {/* Chapter 01 — Introduction */}
      <section className="container-x py-8 md:py-12">
        <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
          <div>
            <Reveal>
              <span className="mono-label text-brand">01 — Heritage</span>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display text-3xl font-semibold leading-tight tracking-tighter text-navy sm:text-4xl">
                The core of quality CRGO electrical steel, engineered since 1977.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-lg leading-relaxed text-steel">
                From a single vision in 1977 to a distinguished global leader across three
                generations — Banmore Electricals manufactures and exports quality CRGO transformer
                core lamination and built-core assembly. We were the first company in India certified
                (COC) by the Bureau of Indian Standards for CRGO transformer lamination, and hold the
                Government of India’s prestigious Star Export House status.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/about">
                  <Button
                    data-testid="home-about-btn"
                    className="btn-sweep h-12 rounded-none bg-navy px-7 text-white hover:bg-navy"
                  >
                    Our Story <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="relative">
            <div className="group relative overflow-hidden clip-frame">
              <img
                src={IMAGES.heritage}
                alt="Banmore Electricals manufacturing heritage — transformer core assembly"
                loading="lazy"
                className="img-bw aspect-[4/5] w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-8 -left-4 border border-line bg-white p-6 shadow-xl sm:-left-8 sm:p-8">
              <p className="mono-label text-steel">It all started in</p>
              <p className="mt-2 font-display text-5xl font-bold tracking-tighter text-navy">1977</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Stats band */}
      <section className="bg-navy" data-testid="stats-band">
        <div className="container-x grid grid-cols-2 gap-px lg:grid-cols-4">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className="px-2 py-8 md:px-6">
              <p className="font-display text-5xl font-bold tracking-tighter text-white sm:text-6xl">
                {s.value}
              </p>
              <p className="mt-3 text-sm font-medium text-white">{s.label}</p>
              <p className="mono-label mt-1 text-white/40">{s.sub}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Products bento */}
      <section className="container-x py-8 md:py-12">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <SectionHeading kicker="02 — Products" title="What we manufacture" />
          <Reveal delay={0.1}>
            <Link
              to="/products"
              className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-navy"
              data-testid="products-viewall-link"
            >
              View all products <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 2) * 0.08}>
              <Link
                to={`/products/${p.slug}`}
                data-testid={`product-card-${p.slug}`}
                className="relative flex h-full min-h-[210px] flex-col justify-between overflow-hidden border border-line md:min-h-[235px]"
              >
                <img
                  src={p.image}
                  alt={p.name}
                  loading="lazy"
                  className="img-color absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/95 via-navy/60 to-navy/25" />
                <div className="relative p-6 md:p-7">
                  <span className="mono-label text-brand-light">{p.index}</span>
                </div>
                <div className="relative p-6 pt-0 md:p-7 md:pt-0">
                  <h3 className="font-display text-xl font-semibold tracking-tight text-white">
                    {p.name}
                  </h3>
                  <p className="mt-1.5 max-w-md text-sm leading-snug text-white/80">{p.tagline}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-white">
                    Explore
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Why Banmore */}
      <section className="bg-white py-8 md:py-12" data-testid="why-section">
        <div className="container-x">
          <SectionHeading kicker="03 — Why Banmore" title="Precision you can guarantee" />
          <div className="mt-8 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.08} className="bg-white p-8">
                <span className="font-display text-3xl font-bold tracking-tighter text-line">
                  0{i + 1}
                </span>
                <h3 className="mt-4 font-display text-xl font-semibold text-navy">{w.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-steel">{w.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Manufacturing facility */}
      <section className="container-x py-8 md:py-12">
        <div className="grid items-center gap-16 lg:grid-cols-2 lg:gap-24">
          <Reveal className="group relative order-2 overflow-hidden clip-frame lg:order-1">
            <img
              src={IMAGES.cncLine}
              alt="Fully automated CNC & slitting line"
              loading="lazy"
              className="img-color aspect-[3/2] w-full object-cover"
            />
          </Reveal>
          <div className="order-1 lg:order-2">
            <Reveal>
              <span className="mono-label text-brand">04 — Facility & Capacity</span>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display text-3xl font-semibold leading-tight tracking-tighter text-navy sm:text-4xl">
                Fully automated CNC & slitting lines.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-lg leading-relaxed text-steel">
                State-of-the-art CNC cut-to-length lines and precision slitting lines up to 1250 mm
                width, producing 6000 MT per year at 700 MT per month — with complete traceability from
                incoming coil to finished core.
              </p>
            </Reveal>
            <div className="mt-8 space-y-4">
              {[
                'CNC automated cut-to-length lamination lines',
                'Precision slitting up to 1250 mm width',
                'Dedicated CRGO testing laboratory',
                'Full coil-to-product traceability',
              ].map((item, i) => (
                <Reveal key={item} delay={0.25 + i * 0.06}>
                  <div className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-brand" />
                    <span className="text-navy">{item}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Global exports */}
      <section className="bg-navy py-24 text-white md:py-32" data-testid="exports-section">
        <div className="container-x">
          <SectionHeading kicker="05 — Global Exports" title="Trusted across 5 continents" light />
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/60">
            A Government of India recognised Star Export House, delivering export-grade CRGO
            transformer core to 16 countries worldwide.
          </p>
          <div className="mt-8 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-5">
            {EXPORT_REGIONS.map((r, i) => (
              <Reveal key={r.region} delay={i * 0.06} className="bg-navy p-8">
                <p className="font-display text-4xl font-bold tracking-tighter text-brand-light">
                  {r.countries}
                </p>
                <p className="mt-3 text-sm font-medium text-white">{r.region}</p>
                <p className="mono-label mt-1 text-white/40">Countries</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/export-markets">
              <Button
                data-testid="exports-cta"
                variant="outline"
                className="h-12 rounded-none border-white/30 bg-transparent px-7 text-white hover:bg-white hover:text-navy"
              >
                Explore export markets <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="container-x py-8 md:py-12">
        <SectionHeading kicker="06 — Certifications" title="Certified compliance" />
        <div className="mt-8 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
          {CERTIFICATIONS.map((c, i) => (
            <Reveal key={c.name} delay={i * 0.05} className="flex items-center gap-5 bg-white p-8">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center border border-line font-display text-lg font-bold text-brand">
                ✓
              </span>
              <div>
                <p className="font-display text-lg font-semibold text-navy">{c.name}</p>
                <p className="mt-1 text-sm text-steel">{c.detail}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Industries + Testimonials */}
      <section className="bg-white py-8 md:py-12">
        <div className="container-x grid gap-16 lg:grid-cols-[1fr_1.3fr] lg:gap-24">
          <div>
            <SectionHeading kicker="07 — Industries" title="Industries we serve" />
            <div className="mt-8 flex flex-wrap gap-3">
              {INDUSTRIES.map((ind, i) => (
                <Reveal key={ind} delay={i * 0.05}>
                  <span className="inline-block border border-line px-5 py-3 text-sm font-medium text-navy transition-colors hover:border-brand hover:text-brand">
                    {ind}
                  </span>
                </Reveal>
              ))}
            </div>
          </div>
          <div>
            <SectionHeading kicker="08 — Testimonials" title="What partners say" />
            <div className="mt-8 space-y-6">
              {TESTIMONIALS.map((t, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <figure className="border-l-2 border-brand bg-mist p-8">
                    <blockquote className="font-display text-lg leading-relaxed text-navy">
                      “{t.quote}”
                    </blockquote>
                    <figcaption className="mono-label mt-4 text-steel">
                      {t.author} · {t.org}
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Latest news */}
      <section className="container-x py-8 md:py-12">
        <SectionHeading kicker="09 — Latest News" title="Milestones & updates" />
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {NEWS.map((n, i) => (
            <Reveal key={n.title} delay={i * 0.08}>
              <article className="group flex h-full flex-col border border-line bg-white p-8 transition-colors hover:border-navy">
                <div className="flex items-center justify-between">
                  <span className="mono-label text-brand">{n.tag}</span>
                  <span className="mono-label text-steel">{n.date}</span>
                </div>
                <h3 className="mt-6 font-display text-xl font-semibold text-navy">{n.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-steel">{n.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-brand py-24 text-white md:py-32">
        <div className="grain absolute inset-0" />
        <div className="container-x relative text-center">
          <Reveal>
            <h2 className="mx-auto max-w-4xl font-display text-4xl font-semibold tracking-tighter sm:text-5xl lg:text-6xl">
              Need CRGO transformer core with guaranteed losses?
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mx-auto mt-6 max-w-xl text-lg text-white/80">
              Talk to our engineers about your grade, size and quantity requirements.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
